import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ChevronDown, Plus, Search, Trash2, UploadCloud, X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { restaurantAdminService, rowsOf } from "../../services/restaurantAdmin.service";
import { isBase64Image, resolveRestaurantFoodImageUrl } from "../../utils/restaurantFoodImageUrl";
import FoodCatalogManagerDrawer from "./FoodCatalogManagerDrawer";

function Label({ children }) {
  return <div className="mb-1 text-[12px] font-medium text-slate-500">{children}</div>;
}
function Input({ className = "", ...props }) {
  return <input {...props} className={"h-[44px] w-full rounded-[8px] border border-[#f2c6cc] bg-white px-4 text-[12px] text-slate-600 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db] " + className} />;
}
function Textarea({ className = "", ...props }) {
  return <textarea {...props} className={"min-h-[92px] w-full rounded-[8px] border border-[#f2c6cc] bg-white px-4 py-3 text-[12px] text-slate-600 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db] " + className} />;
}
function SelectLike({ value, onChange, placeholder, options = [], disabled = false }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = options.find((o) => String(o.value) === String(value));

  return (
    <div ref={dropdownRef} className="relative min-w-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`h-[44px] w-full rounded-xl border px-4 pr-10 text-left text-[12px] outline-none transition ${
          disabled
            ? "cursor-not-allowed border-[#f2c6cc] bg-slate-50 text-slate-400"
            : "border-[#f2c6cc] bg-white text-slate-600 hover:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
        }`}
      >
        {selected?.label || placeholder}
      </button>

      <ChevronDown
        size={16}
        className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition ${open ? "rotate-180" : ""}`}
      />

      {open && !disabled ? (
        <div className="absolute z-50 mt-2 max-h-60 w-full overflow-y-auto rounded-xl border border-[#f2c6cc] bg-white shadow-xl">
          <button
            type="button"
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            className="w-full px-4 py-3 text-left text-[12px] text-slate-500 hover:bg-[#fff4f6] hover:text-[#d86a78]"
          >
            {placeholder}
          </button>
          {options.map((o) => {
            const active = String(value) === String(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={`w-full px-4 py-3 text-left text-[12px] transition ${
                  active ? "bg-[#d86a78] text-white" : "text-slate-600 hover:bg-[#fff4f6] hover:text-[#d86a78]"
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
function RestaurantMultiSelect({ restaurants = [], selectedIds = [], search = "", onSearch, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selectedKeys = selectedIds.map(String);
  const selectedRestaurants = restaurants.filter((r) => selectedKeys.includes(String(idOf(r))));
  const q = String(search || "").trim().toLowerCase();
  const visibleRestaurants = restaurants.filter((r) => {
    if (!q) return true;
    return [r.name, r.restaurantName, r.restaurant_name, r.code, r.city, r.state, r.phone]
      .some((v) => String(v || "").toLowerCase().includes(q));
  });

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className="relative">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <Label>Select Restaurants</Label>
        <div className="text-[11px] font-medium text-slate-400">
          {selectedRestaurants.length ? `${selectedRestaurants.length} selected` : "No restaurant selected"}
        </div>
      </div>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-[46px] w-full items-center justify-between gap-3 rounded-[10px] border border-[#f2c6cc] bg-white px-4 py-2 text-left transition hover:border-[#d86a78] focus:outline-none focus:ring-2 focus:ring-[#f7d7db]"
      >
        <div className="min-w-0">
          <div className="truncate text-[12px] font-medium text-slate-600">
            {selectedRestaurants.length
              ? selectedRestaurants.slice(0, 3).map(nameOf).join(", ")
              : "Choose restaurants from dropdown"}
          </div>
          <div className="mt-0.5 text-[11px] text-slate-400">
            {selectedRestaurants.length > 3 ? `+${selectedRestaurants.length - 3} more selected` : "Search, tick, and keep the page clean"}
          </div>
        </div>
        <ChevronDown size={16} className={`shrink-0 text-slate-400 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open ? (
        <div className="relative z-10 mt-2 w-full overflow-hidden rounded-[14px] border border-[#f2c6cc] bg-white shadow-xl">
          <div className="border-b border-[#f7d7db] bg-white p-3">
            <div className="relative">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder="Search restaurant by name, code or city"
                className="h-[38px] w-full rounded-[9px] border border-[#f2c6cc] bg-white pl-9 pr-3 text-[12px] text-slate-600 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db]"
              />
            </div>
          </div>

          <div className="max-h-[220px] overflow-y-auto overscroll-contain p-2 pr-1">
            {visibleRestaurants.length ? (
              visibleRestaurants.map((r) => {
                const rid = idOf(r);
                const active = selectedKeys.includes(String(rid));
                return (
                  <button
                    type="button"
                    key={rid}
                    onClick={() => onToggle(rid)}
                    className={`mb-1 flex w-full items-center justify-between gap-3 rounded-[10px] border px-3 py-2 text-left transition last:mb-0 ${
                      active ? "border-[#d86a78] bg-[#fff0f2]" : "border-transparent bg-white hover:border-[#f2c6cc] hover:bg-[#fffafb]"
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="truncate text-[12px] font-medium text-slate-700">{nameOf(r)}</div>
                      <div className="truncate text-[11px] text-slate-400">{r.code || r.city || r.state || "Restaurant"}</div>
                    </div>
                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] ${active ? "border-[#d86a78] bg-[#d86a78] text-white" : "border-[#f2c6cc] bg-white text-white"}`}>
                      ✓
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-6 text-center text-[12px] text-slate-400">No restaurant found</div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-[#f7d7db] bg-[#fffafb] px-3 py-2">
            <span className="text-[11px] text-slate-500">{selectedRestaurants.length} selected</span>
            {selectedRestaurants.length ? (
              <button type="button" onClick={onClear} className="text-[11px] font-medium text-[#d86a78] hover:underline">
                Clear all
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {selectedRestaurants.length ? (
        <div className="mt-3 max-h-[112px] overflow-y-auto rounded-[12px] border border-[#f7d7db] bg-white p-2">
          <div className="flex flex-wrap gap-2">
            {selectedRestaurants.map((r) => {
              const rid = idOf(r);
              return (
                <span key={rid} className="inline-flex max-w-full items-center gap-2 rounded-full border border-[#f2c6cc] bg-[#fffafb] px-3 py-1.5 text-[12px] text-slate-600">
                  <span className="max-w-[220px] truncate">{nameOf(r)}</span>
                  <button type="button" onClick={() => onToggle(rid)} className="text-[#d86a78] hover:text-[#b94b59]" aria-label={`Remove ${nameOf(r)}`}>
                    <X size={13} />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="mt-3 rounded-[12px] border border-dashed border-[#f2c6cc] bg-white px-4 py-4 text-center text-[12px] text-slate-400">
          Select restaurants from the dropdown. This section will not grow even when restaurants become high volume.
        </div>
      )}
    </div>
  );
}
function Toggle({ value, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <button type="button" onClick={() => onChange(!value)} className={`relative h-[22px] w-[46px] rounded-full transition ${value ? "bg-[#d86a78]" : "bg-slate-300"}`}>
        <span className={`absolute top-[3px] h-[16px] w-[16px] rounded-full bg-white shadow transition ${value ? "left-[27px]" : "left-[3px]"}`} />
      </button>
      <div className={`text-[12px] font-semibold ${value ? "text-emerald-600" : "text-rose-600"}`}>{value ? "Yes" : "No"}</div>
    </div>
  );
}
function Button({ children, variant = "primary", className = "", ...props }) {
  const cls = variant === "primary" ? "bg-[#d86a78] text-white hover:bg-[#c95766]" : "border border-[#f2c6cc] bg-white text-[#d86a78] hover:bg-[#fff6f7]";
  return <button type="button" {...props} className={`inline-flex h-[44px] items-center justify-center gap-2 rounded-[10px] px-5 text-[13px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${cls} ${className}`}>{children}</button>;
}

function ThemePopup({ type = "error", message = "", onClose }) {
  if (!message) return null;
  const isSuccess = type === "success";
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/35 px-4">
      <div className="w-full max-w-[430px] overflow-hidden rounded-[22px] border border-[#f2c6cc] bg-white shadow-2xl">
        <div className="bg-gradient-to-r from-[#fff4f6] to-white px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className={`text-[17px] font-semibold ${isSuccess ? "text-emerald-600" : "text-[#d86a78]"}`}>
                {isSuccess ? "Success" : "Please Check Details"}
              </div>
              <div className="mt-1 text-[12px] text-slate-500">{isSuccess ? "Your changes have been saved." : "Fix this field and try again."}</div>
            </div>
            <button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm transition hover:bg-[#fff0f2] hover:text-[#d86a78]">
              <X size={17} />
            </button>
          </div>
        </div>
        <div className="px-5 py-5">
          <div className={`rounded-[16px] border px-4 py-3 text-[13px] font-medium leading-6 ${isSuccess ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-[#f2c6cc] bg-[#fffafb] text-slate-700"}`}>
            {message}
          </div>
          <div className="mt-5 flex justify-end">
            <button type="button" onClick={onClose} className={`h-[40px] rounded-[12px] px-5 text-[13px] font-semibold text-white transition ${isSuccess ? "bg-emerald-600 hover:bg-emerald-700" : "bg-[#d86a78] hover:bg-[#c95766]"}`}>
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function toSlug(s) {
  const out = String(s || "").toLowerCase().trim().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  return out || "unknown";
}
function toEditableSlug(s) {
  return String(s || "").toLowerCase().trim().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
function idOf(row) { return row?.id || row?.restaurantId || row?.restaurant_id || row?.foodItemId || row?.food_item_id || ""; }
function nameOf(row) { return row?.name || row?.restaurantName || row?.restaurant_name || "Restaurant"; }

const FOOD_TYPES = ["VEG", "NON_VEG", "EGG"];
const TAX_OPTIONS = ["0", "5", "12", "18", "28"];
const GROUP_OPTIONS = ["size", "plate", "flavor", "pack", "weight", "volume", "combo", "type", "spice", "base", "crust"];
const PACK_LABEL_OPTIONS = ["Plate", "Half Plate", "Full Plate", "Piece", "Pack", "Combo", "Small", "Medium", "Large", "Regular", "Family Pack", "Bottle", "Can"];
const UNIT_OPTIONS = ["pcs", "plate", "pack", "box", "bottle", "can", "g", "kg", "ml", "L"];
const WEIGHT_OPTIONS = ["g", "kg", "ml", "L"];

function blankVariant(index = 0) {
  return {
    variantName: "",
    variantSku: "",
    barcode: "",
    variantGroup: "size",
    variantValue: "",
    packLabel: "",
    qtyPackValue: "",
    qtyPackUnit: "pcs",
    weightValue: "",
    weightUnitSymbol: "g",
    foodType: "VEG",
    mrp: "",
    sellingPrice: "",
    taxPercent: 5,
    imageUrl: "",
    images: [],
    shortDescription: "",
    description: "",
    isDefault: index === 0,
    isActive: true,
    sortOrder: index,
  };
}

const blankForm = {
  sku: "",
  barcode: "",
  name: "",
  slug: "",
  status: "active",
  categoryId: "",
  subcategoryId: "",
  tags: [],
  shortDescription: "",
  description: "",
  imageUrl: "",
  images: [],
  isActive: true,
  restaurantIds: [],
  restaurantSearch: "",
  preparationTimeMins: 20,
  restaurantPrice: "",
  isAvailable: true,
  variants: [blankVariant(0)],
};

function normalizeVariant(variant = {}, index = 0) {
  return {
    id: variant.id || "",
    variantName: variant.variantName || variant.variant_name || "",
    variantSku: variant.variantSku || variant.variant_sku || "",
    barcode: variant.barcode || "",
    variantGroup: variant.variantGroup || variant.variant_group || "size",
    variantValue: variant.variantValue || variant.variant_value || "",
    packLabel: variant.packLabel || variant.pack_label || "",
    qtyPackValue: variant.qtyPackValue ?? variant.qty_pack_value ?? "",
    qtyPackUnit: variant.qtyPackUnit || variant.qty_pack_unit || "pcs",
    weightValue: variant.weightValue ?? variant.weight_value ?? "",
    weightUnitSymbol: variant.weightUnitSymbol || variant.weight_unit_symbol || "g",
    foodType: variant.foodType || variant.food_type || "VEG",
    mrp: variant.mrp ?? "",
    sellingPrice: variant.sellingPrice ?? variant.selling_price ?? "",
    taxPercent: variant.taxPercent ?? variant.tax_percent ?? 5,
    imageUrl: variant.imageUrl || variant.image_url || normalizeImages(variant.images || variant.meta?.images)[0] || "",
    images: normalizeImages(variant.images || variant.meta?.images || variant.imageUrl || variant.image_url),
    shortDescription: variant.shortDescription || variant.short_description || "",
    description: variant.description || "",
    isDefault: Boolean(variant.isDefault ?? variant.is_default ?? index === 0),
    isActive: variant.isActive ?? variant.is_active ?? true,
    sortOrder: variant.sortOrder ?? variant.sort_order ?? index,
  };
}

function tagsOf(row) {
  if (Array.isArray(row?.tags)) return row.tags;
  if (!row?.tags) return [];
  try { const parsed = JSON.parse(row.tags); if (Array.isArray(parsed)) return parsed; } catch {}
  return String(row.tags).split(",").map((x) => x.trim()).filter(Boolean);
}

function toNumber(value) {
  if (value === "" || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function textValue(value) {
  return String(value ?? "").trim();
}

function isText(value) {
  return /[a-zA-Z]/.test(textValue(value));
}

function isAlphaNumericCode(value) {
  return /^[a-zA-Z0-9_-]+$/.test(textValue(value));
}

function isSafeAmount(value) {
  return /^\d{1,8}(\.\d{1,2})?$/.test(textValue(value));
}

function isSafeOptionalAmount(value) {
  const text = textValue(value);
  return text === "" || isSafeAmount(text);
}

function isPositiveSafeAmount(value) {
  return isSafeAmount(value) && Number(value) > 0;
}

function isPositiveInteger(value) {
  return /^\d+$/.test(textValue(value)) && Number(value) > 0;
}

const MAX_IMAGE_SIZE_MB = 3;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;
const MAX_FOOD_IMAGES = 3;
const MAX_VARIANT_IMAGES = 3;

function normalizeImages(value) {
  if (Array.isArray(value)) return value.map((x) => String(x || "").trim()).filter((x) => x && !isBase64Image(x)).slice(0, 3);
  if (typeof value === "string") {
    const clean = value.trim();
    if (!clean) return [];
    try {
      const parsed = JSON.parse(clean);
      if (Array.isArray(parsed)) return parsed.map((x) => String(x || "").trim()).filter((x) => x && !isBase64Image(x)).slice(0, 3);
    } catch {}
    return isBase64Image(clean) ? [] : [clean];
  }
  return [];
}

async function uploadFileToImageUrl(file) {
  if (!String(file.type || "").startsWith("image/")) throw new Error("Only image files are allowed");
  if (file.size > MAX_IMAGE_SIZE_BYTES) throw new Error(`Each image must be ${MAX_IMAGE_SIZE_MB} MB or less`);
  const res = await restaurantAdminService.uploadFoodImage(file);
  const url = res?.data?.imageUrl || res?.data?.url || res?.imageUrl || res?.url;
  if (!url) throw new Error("Upload failed: image URL missing");
  return url;
}

async function filesToImageUrls(files, { maxCount = 1, existingCount = 0 } = {}) {
  const selected = Array.from(files || []);
  if (!selected.length) return [];
  if (selected.length + existingCount > maxCount) {
    throw new Error(`Maximum ${maxCount} image${maxCount > 1 ? "s" : ""} allowed`);
  }
  return Promise.all(selected.map(uploadFileToImageUrl));
}

function ImageUploader({ label, images = [], maxCount = 1, required = false, coverImage = "", onCoverChange, onChange, onError }) {
  const inputId = React.useId();
  const safeImages = normalizeImages(images);
  async function handleFiles(e) {
    try {
      const next = await filesToImageUrls(e.target.files, { maxCount, existingCount: safeImages.length });
      onChange([...(safeImages || []), ...next].slice(0, maxCount));
    } catch (error) {
      onError?.(error?.message || "Invalid image");
    } finally {
      e.target.value = "";
    }
  }
  return (
    <div className="rounded-[14px] border border-dashed border-[#f2c6cc] bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[13px] font-semibold text-slate-700">{label}{required ? " *" : ""}</div>
          <div className="text-[11px] text-slate-500">Upload {maxCount === 1 ? "1 image" : `up to ${maxCount} images`} • Max {MAX_IMAGE_SIZE_MB} MB each</div>
        </div>
        <label htmlFor={inputId} className="inline-flex h-[38px] cursor-pointer items-center justify-center gap-2 rounded-[10px] border border-[#f2c6cc] bg-white px-4 text-[12px] font-semibold text-[#d86a78] hover:bg-[#fff6f7]">
          <UploadCloud size={15} /> Choose Image{maxCount > 1 ? "s" : ""} ({safeImages.length}/{maxCount})
        </label>
        <input id={inputId} type="file" accept="image/*" multiple={maxCount > 1} className="hidden" onChange={handleFiles} />
      </div>
      {safeImages.length ? (
        <div className="flex flex-wrap gap-3">
          {safeImages.map((src, idx) => {
            const previewSrc = resolveRestaurantFoodImageUrl(src);
            const isCover = String(coverImage || safeImages[0] || "") === String(src);
            return (
              <div key={`${src.slice(0, 32)}-${idx}`} className="w-[118px] rounded-[12px] border border-[#f2c6cc] bg-white p-2">
                <div className="relative h-[84px] w-full overflow-hidden rounded-[10px] bg-[#fffafb]">
                  {previewSrc ? <img src={previewSrc} alt="preview" className="h-full w-full object-contain p-1" /> : <div className="flex h-full w-full items-center justify-center text-[10px] font-medium text-slate-400">Image</div>}
                  <button type="button" onClick={() => onChange(safeImages.filter((_, i) => i !== idx))} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-rose-600 shadow">
                    <Trash2 size={13} />
                  </button>
                </div>
                {onCoverChange ? (
                  <button
                    type="button"
                    onClick={() => onCoverChange(src)}
                    className={`mt-2 h-8 w-full rounded-[9px] border text-[11px] font-semibold ${
                      isCover
                        ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                        : "border-[#f2c6cc] bg-[#fff6f7] text-[#d86a78] hover:bg-[#fff0f2]"
                    }`}
                  >
                    {isCover ? "Cover Image" : "Set Cover"}
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-[12px] bg-[#fffafb] px-4 py-5 text-center text-[12px] font-medium text-slate-400">No image selected</div>
      )}
    </div>
  );
}

export default function FoodAddItem() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editMode = Boolean(id);
  const [form, setForm] = useState(blankForm);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const categoryOptions = categories.map((c) => ({ value: c.id, label: c.name }));
  const subcategoryOptions = useMemo(() => {
    const list = form.categoryId ? subcategories.filter((s) => String(s.categoryId || s.category_id) === String(form.categoryId)) : subcategories;
    return list.map((s) => ({ value: s.id, label: s.name }));
  }, [form.categoryId, subcategories]);

  async function loadMeta() {
    const [catRes, subRes, tagRes, restRes] = await Promise.all([
      restaurantAdminService.listFoodCategories({ includeInactive: true }),
      restaurantAdminService.listFoodSubcategories({ includeInactive: true }),
      restaurantAdminService.listFoodTags({ includeInactive: true }),
      restaurantAdminService.listRestaurants({ limit: 300 }),
    ]);
    setCategories(rowsOf(catRes));
    setSubcategories(rowsOf(subRes));
    setTags(rowsOf(tagRes));
    setRestaurants(rowsOf(restRes));
  }

  async function loadExisting() {
    if (!editMode) return;
    const res = await restaurantAdminService.listFoodItems({ limit: 500 });
    const row = rowsOf(res).find((item) => String(idOf(item)) === String(id));
    if (!row) return;
    setForm((prev) => ({
      ...prev,
      sku: row.sku || "",
      barcode: row.barcode || "",
      name: row.name || row.foodName || "",
      slug: row.slug || toSlug(row.name || row.foodName),
      status: row.status || "active",
      categoryId: row.categoryId || row.category_id || "",
      subcategoryId: row.subcategoryId || row.subcategory_id || "",
      tags: tagsOf(row),
      shortDescription: row.shortDescription || row.short_description || "",
      description: row.description || "",
      imageUrl: row.imageUrl || row.image_url || normalizeImages(row.images || row.meta?.images)[0] || "",
      images: normalizeImages(row.images || row.meta?.images || row.imageUrl || row.image_url),
      isActive: row.isActive ?? row.is_active ?? true,
      variants: Array.isArray(row.variants) && row.variants.length ? row.variants.map(normalizeVariant) : [blankVariant(0)],
    }));
  }

  async function loadAll() {
    setLoading(true);
    setError("");
    try {
      await loadMeta();
      await loadExisting();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Unable to load food form");
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [id]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value, ...(key === "name" && !prev.slug ? { slug: toEditableSlug(value) } : {}) }));
  }
  function setVariant(index, patch) {
    setForm((prev) => ({ ...prev, variants: prev.variants.map((v, i) => (i === index ? { ...v, ...patch } : v)) }));
  }
  function addVariant() {
    setForm((prev) => ({ ...prev, variants: [...prev.variants, blankVariant(prev.variants.length)] }));
  }
  function removeVariant(index) {
    setForm((prev) => {
      if (prev.variants.length <= 1) return { ...prev, variants: [blankVariant(0)] };
      const next = prev.variants
        .filter((_, i) => i !== index)
        .map((v, i) => ({ ...v, sortOrder: i, isDefault: false }));
      if (!next.some((v) => v.isDefault)) next[0].isDefault = true;
      return { ...prev, variants: next };
    });
  }
  function makeDefaultVariant(index) {
    setForm((prev) => ({ ...prev, variants: prev.variants.map((v, i) => ({ ...v, isDefault: i === index })) }));
  }
  function toggleTag(name) {
    const key = String(name || "").trim();
    if (!key) return;
    setForm((prev) => {
      const has = prev.tags.map((x) => x.toLowerCase()).includes(key.toLowerCase());
      return { ...prev, tags: has ? prev.tags.filter((x) => x.toLowerCase() !== key.toLowerCase()) : [...prev.tags, key] };
    });
  }
  function toggleRestaurant(restaurantId) {
    setForm((prev) => {
      const key = String(restaurantId);
      const has = prev.restaurantIds.map(String).includes(key);
      return { ...prev, restaurantIds: has ? prev.restaurantIds.filter((x) => String(x) !== key) : [...prev.restaurantIds, key] };
    });
  }
  function setFoodImages(imgs) {
    const nextImages = normalizeImages(imgs);
    setForm((prev) => ({
      ...prev,
      images: nextImages,
      imageUrl: nextImages.includes(prev.imageUrl) ? prev.imageUrl : nextImages[0] || "",
    }));
  }

  function validate() {
    const sku = textValue(form.sku);
    const barcode = textValue(form.barcode);
    const name = textValue(form.name);
    const slug = textValue(form.slug);
    const foodImages = normalizeImages(form.images);

    if (!sku) throw new Error("SKU is required");
    if (!isAlphaNumericCode(sku)) throw new Error("SKU can contain only letters, numbers, hyphen and underscore.");
    if (barcode && !/^\d{1,15}$/.test(barcode)) throw new Error("Barcode must contain numbers only, maximum 15 digits.");
    if (!name) throw new Error("Food name is required");
    if (!isText(name)) throw new Error("Food name must contain text.");
    if (!slug) throw new Error("Slug is required");
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) throw new Error("Slug can contain lowercase letters, numbers and hyphen only. Example: paneer-tikka");
    if (!form.categoryId) throw new Error("Main category is required");
    if (!form.subcategoryId) throw new Error("Subcategory is required");
    if (!foodImages.length) throw new Error("Please upload at least 1 food image.");
    if (foodImages.length > MAX_FOOD_IMAGES) throw new Error(`Maximum ${MAX_FOOD_IMAGES} food images allowed.`);
    if (!Array.isArray(form.variants) || !form.variants.length) throw new Error("At least one actual variant is required");
    if (!isSafeOptionalAmount(form.restaurantPrice)) throw new Error("Restaurant price override must be a valid number. Maximum 8 digits and 2 decimals allowed.");
    if (textValue(form.restaurantPrice) && Number(form.restaurantPrice) <= 0) throw new Error("Restaurant price override must be greater than 0.");
    if (!isPositiveInteger(form.preparationTimeMins)) throw new Error("Preparation time is required and must be greater than 0.");
    if (Number(form.preparationTimeMins) > 1440) throw new Error("Preparation time cannot be more than 1440 minutes.");

    const variantKeys = new Set();

    form.variants.forEach((v, index) => {
      const variantNumber = index + 1;
      const variantName = textValue(v.variantName);
      const variantSku = textValue(v.variantSku);
      const variantBarcode = textValue(v.barcode);
      const variantImages = normalizeImages(v.images);

      if (!variantName) throw new Error(`Variant ${variantNumber}: Variant name is required`);
      if (!isText(variantName)) throw new Error(`Variant ${variantNumber}: Variant name must contain text.`);
      if (!variantSku) throw new Error(`Variant ${variantNumber}: Variant SKU is required`);
      if (!isAlphaNumericCode(variantSku)) throw new Error(`Variant ${variantNumber}: Variant SKU can contain only letters, numbers, hyphen and underscore.`);
      if (variantBarcode && !/^\d{1,15}$/.test(variantBarcode)) throw new Error(`Variant ${variantNumber}: Barcode must contain numbers only, maximum 15 digits.`);
      if (!textValue(v.variantGroup)) throw new Error(`Variant ${variantNumber}: Variant group is required`);
      if (!textValue(v.variantValue)) throw new Error(`Variant ${variantNumber}: Variant value is required`);
      if (!textValue(v.packLabel)) throw new Error(`Variant ${variantNumber}: Pack label is required`);
      if (!textValue(v.foodType)) throw new Error(`Variant ${variantNumber}: Food type is required`);
      if (!TAX_OPTIONS.includes(String(v.taxPercent))) throw new Error(`Variant ${variantNumber}: Select a valid tax percent.`);
      if (!isSafeOptionalAmount(v.qtyPackValue)) throw new Error(`Variant ${variantNumber}: Qty / Pack value must be a valid number. Maximum 8 digits and 2 decimals allowed.`);
      if (textValue(v.qtyPackValue) && Number(v.qtyPackValue) <= 0) throw new Error(`Variant ${variantNumber}: Qty / Pack value must be greater than 0.`);
      if (!isSafeOptionalAmount(v.weightValue)) throw new Error(`Variant ${variantNumber}: Weight value must be a valid number. Maximum 8 digits and 2 decimals allowed.`);
      if (textValue(v.weightValue) && Number(v.weightValue) <= 0) throw new Error(`Variant ${variantNumber}: Weight value must be greater than 0.`);
      if (!isPositiveSafeAmount(v.mrp)) throw new Error(`Variant ${variantNumber}: MRP is required and must be greater than 0.`);
      if (!isPositiveSafeAmount(v.sellingPrice)) throw new Error(`Variant ${variantNumber}: Selling price is required and must be greater than 0.`);
      if (Number(v.sellingPrice) > Number(v.mrp)) throw new Error(`Variant ${variantNumber}: Selling price cannot be greater than MRP`);
      if (!variantImages.length) throw new Error(`Variant ${variantNumber} (${variantName}): Please upload at least 1 image.`);
      if (variantImages.length > MAX_VARIANT_IMAGES) throw new Error(`Variant ${variantNumber}: maximum ${MAX_VARIANT_IMAGES} images allowed`);

      const key = `${variantName.toLowerCase()}::${variantSku.toLowerCase()}`;
      if (variantKeys.has(key)) throw new Error(`Variant ${variantNumber}: Duplicate variant name/SKU found. Please make variant name and SKU different.`);
      variantKeys.add(key);
    });
  }

  async function submit() {
    try {
      validate();
      setSaving(true);
      setError("");
      setSuccess("");
      const defaultVariant = form.variants.find((v) => v.isDefault) || form.variants[0];
      const payload = {
        sku: form.sku,
        barcode: form.barcode || undefined,
        name: form.name,
        slug: form.slug || toSlug(form.name),
        categoryId: form.categoryId,
        subcategoryId: form.subcategoryId,
        tags: form.tags,
        shortDescription: form.shortDescription,
        description: form.description,
        imageUrl: normalizeImages(form.images).includes(form.imageUrl) ? form.imageUrl : normalizeImages(form.images)[0] || defaultVariant.imageUrl || "",
        images: normalizeImages(form.images),
        foodType: defaultVariant.foodType,
        basePrice: Number(defaultVariant.sellingPrice || 0),
        mrp: Number(defaultVariant.mrp || 0),
        taxPercent: Number(defaultVariant.taxPercent || 5),
        status: form.status,
        isActive: form.isActive,
        restaurantIds: form.restaurantIds,
        restaurantPrice: toNumber(form.restaurantPrice),
        preparationTimeMins: toNumber(form.preparationTimeMins),
        isAvailable: form.isAvailable,
        variants: form.variants.map((v, index) => ({
          ...v,
          imageUrl: normalizeImages(v.images)[0] || v.imageUrl || "",
          images: normalizeImages(v.images),
          meta: { ...(v.meta || {}), images: normalizeImages(v.images) },
          sortOrder: index,
          mrp: Number(v.mrp || 0),
          sellingPrice: Number(v.sellingPrice || 0),
          taxPercent: Number(v.taxPercent || 5),
          qtyPackValue: toNumber(v.qtyPackValue),
          weightValue: toNumber(v.weightValue),
        })),
      };
      if (editMode) await restaurantAdminService.updateFoodItem(id, payload);
      else await restaurantAdminService.createFoodItem(payload);
      setSuccess(editMode ? "Item updated successfully" : "Item created successfully");
      setTimeout(() => navigate("/admin/food-dashboard/items"), 700);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Item save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="w-full max-w-full overflow-hidden px-0 pb-24">
      <FoodCatalogManagerDrawer open={catalogOpen} onClose={() => setCatalogOpen(false)} onSaved={loadMeta} />
      <ThemePopup
        type={success ? "success" : "error"}
        message={success || error}
        onClose={() => {
          setError("");
          setSuccess("");
        }}
      />

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate("/admin/food-dashboard/items")} className="flex h-[38px] w-[38px] items-center justify-center rounded-full border border-[#f2c6cc] bg-white text-[#d86a78] hover:bg-[#fff0f2]">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-[25px] font-bold uppercase tracking-wide text-[#d86a78]">{editMode ? "Edit Item" : "Add Item"}</h1>
        </div>
        <Button variant="ghost" onClick={() => setCatalogOpen(true)}>Manage Catalog</Button>
      </div>

      <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-4 pb-8 shadow-sm sm:p-6 sm:pb-10">
        {loading ? <div className="py-12 text-center text-[13px] font-medium text-slate-400">Loading...</div> : (
          <>
            <section className="mb-6 rounded-[18px] border border-[#f2c6cc] bg-[#fffafb] p-5">
              <div className="mb-4">
                <h2 className="text-[18px] font-semibold text-[#d86a78]">Basic Information</h2>
                <p className="text-[12px] text-slate-500">Add common catalog details. Actual sellable items are variants below.</p>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div><Label>SKU *</Label><Input value={form.sku} onChange={(e) => setField("sku", e.target.value)} placeholder="FOOD-AALU-PARATHA" /></div>
                <div><Label>Barcode</Label><Input value={form.barcode} maxLength={15} onChange={(e) => { const value = e.target.value; if (/^\d{0,15}$/.test(value)) setField("barcode", value); }} placeholder="Optional" inputMode="numeric" /></div>
                <div><Label>Status</Label><SelectLike value={form.status} onChange={(v) => setField("status", v)} placeholder="Status" options={[{ value: "active", label: "active" }, { value: "inactive", label: "inactive" }]} /></div>
                <div className="lg:col-span-2"><Label>Food Name *</Label><Input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Aalu Paratha" /></div>
                <div><Label>Slug *</Label><Input value={form.slug} onChange={(e) => setField("slug", toEditableSlug(e.target.value))} placeholder="aalu-paratha" /></div>
                <div><Label>Main Category *</Label><SelectLike value={form.categoryId} onChange={(v) => { setField("categoryId", v); setField("subcategoryId", ""); }} placeholder="Select main category" options={categoryOptions} /></div>
                <div><Label>Subcategory *</Label><SelectLike value={form.subcategoryId} onChange={(v) => setField("subcategoryId", v)} placeholder={form.categoryId ? "Select subcategory" : "Select category first"} options={subcategoryOptions} disabled={!form.categoryId} /></div>
                <div><Label>Active</Label><Toggle value={form.isActive} onChange={(v) => setField("isActive", v)} /></div>
                <div className="lg:col-span-3"><Label>Food Tags</Label><div className="flex flex-wrap gap-2">{tags.map((tag) => { const name = tag.name || tag.tagName; const active = form.tags.map((x) => x.toLowerCase()).includes(String(name).toLowerCase()); return <button key={tag.id || name} type="button" onClick={() => toggleTag(name)} className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold ${active ? "border-[#d86a78] bg-[#fff0f2] text-[#d86a78]" : "border-[#f2c6cc] bg-white text-slate-500 hover:bg-[#fff6f7]"}`}>{name}</button>; })}</div></div>
                <div className="lg:col-span-3"><ImageUploader label="Food Images" required maxCount={MAX_FOOD_IMAGES} images={form.images} coverImage={form.imageUrl} onCoverChange={(src) => setField("imageUrl", src)} onChange={setFoodImages} onError={setError} /></div>
                <div className="lg:col-span-2"><Label>Short Description</Label><Input value={form.shortDescription} onChange={(e) => setField("shortDescription", e.target.value)} placeholder="Fresh stuffed paratha" /></div>
                <div className="lg:col-span-3"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="Common description" /></div>
              </div>

            </section>

            <section className="mb-6 rounded-[18px] border border-[#f2c6cc] bg-[#fffafb] p-5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-[18px] font-semibold text-[#d86a78]">Variant System</h2>
                  <p className="text-[12px] text-slate-500">Same solid catalog variant pattern. Example: Half Plate, Full Plate, Regular, Family Pack.</p>
                </div>
                <Button variant="ghost" onClick={addVariant}><Plus size={16} /> Add Variant</Button>
              </div>

              <div className="space-y-5">
                {form.variants.map((variant, index) => (
                  <div key={index} className="rounded-[18px] border border-[#f2c6cc] bg-white p-5">
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <div className="text-[15px] font-semibold text-slate-700">Variant #{index + 1}</div>
                      <div className="flex gap-2">
                        <Button variant={variant.isDefault ? "primary" : "ghost"} onClick={() => makeDefaultVariant(index)}>{variant.isDefault ? "Default" : "Make Default"}</Button>
                        <Button variant="ghost" onClick={() => removeVariant(index)}><Trash2 size={15} /></Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                      <div><Label>Variant Name *</Label><Input value={variant.variantName} onChange={(e) => setVariant(index, { variantName: e.target.value })} placeholder="Half Plate" /></div>
                      <div><Label>Variant SKU *</Label><Input value={variant.variantSku} onChange={(e) => setVariant(index, { variantSku: e.target.value })} placeholder="AALU-PARATHA-HALF" /></div>
                      <div><Label>Barcode</Label><Input value={variant.barcode} maxLength={15} onChange={(e) => { const value = e.target.value; if (/^\d{0,15}$/.test(value)) setVariant(index, { barcode: value }); }} placeholder="Optional" inputMode="numeric" /></div>
                      <div><Label>Variant Group *</Label><SelectLike value={variant.variantGroup} onChange={(v) => setVariant(index, { variantGroup: v })} placeholder="Group" options={GROUP_OPTIONS.map((x) => ({ value: x, label: x }))} /></div>
                      <div><Label>Variant Value *</Label><Input value={variant.variantValue} onChange={(e) => setVariant(index, { variantValue: e.target.value })} placeholder="Half" /></div>
                      <div><Label>Pack Label *</Label><SelectLike value={variant.packLabel} onChange={(v) => setVariant(index, { packLabel: v })} placeholder="Pack label" options={PACK_LABEL_OPTIONS.map((x) => ({ value: x, label: x }))} /></div>
                      <div><Label>Qty / Pack Value</Label><Input value={variant.qtyPackValue} onChange={(e) => { const value = e.target.value; if (/^\d{0,8}(\.\d{0,2})?$/.test(value)) setVariant(index, { qtyPackValue: value }); }} placeholder="1" inputMode="decimal" /></div>
                      <div><Label>Qty / Pack Unit</Label><SelectLike value={variant.qtyPackUnit} onChange={(v) => setVariant(index, { qtyPackUnit: v })} placeholder="Unit" options={UNIT_OPTIONS.map((x) => ({ value: x, label: x }))} /></div>
                      <div><Label>Weight Value</Label><Input value={variant.weightValue} onChange={(e) => { const value = e.target.value; if (/^\d{0,8}(\.\d{0,2})?$/.test(value)) setVariant(index, { weightValue: value }); }} placeholder="250" inputMode="decimal" /></div>
                      <div><Label>Weight Unit</Label><SelectLike value={variant.weightUnitSymbol} onChange={(v) => setVariant(index, { weightUnitSymbol: v })} placeholder="Unit" options={WEIGHT_OPTIONS.map((x) => ({ value: x, label: x }))} /></div>
                      <div><Label>Food Type</Label><SelectLike value={variant.foodType} onChange={(v) => setVariant(index, { foodType: v })} placeholder="Food Type" options={FOOD_TYPES.map((x) => ({ value: x, label: x }))} /></div>
                      <div><Label>Tax %</Label><SelectLike value={variant.taxPercent} onChange={(v) => setVariant(index, { taxPercent: v })} placeholder="Tax" options={TAX_OPTIONS.map((x) => ({ value: x, label: `${x}%` }))} /></div>
                      <div><Label>MRP *</Label><Input value={variant.mrp} onChange={(e) => { const value = e.target.value; if (/^\d{0,8}(\.\d{0,2})?$/.test(value)) setVariant(index, { mrp: value }); }} placeholder="0" inputMode="decimal" /></div>
                      <div><Label>Selling Price *</Label><Input value={variant.sellingPrice} onChange={(e) => { const value = e.target.value; if (/^\d{0,8}(\.\d{0,2})?$/.test(value)) setVariant(index, { sellingPrice: value }); }} placeholder="0" inputMode="decimal" /></div>
                      <div className="xl:col-span-3"><ImageUploader label="Variant Images" required maxCount={MAX_VARIANT_IMAGES} images={variant.images} onChange={(imgs) => setVariant(index, { images: imgs, imageUrl: normalizeImages(imgs)[0] || "" })} onError={setError} /></div>
                      <div><Label>Variant Active</Label><Toggle value={Boolean(variant.isActive)} onChange={(v) => setVariant(index, { isActive: v })} /></div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-6 rounded-[18px] border border-[#f2c6cc] bg-[#fffafb] p-5">
              <div className="mb-4">
                <h2 className="text-[18px] font-semibold text-[#d86a78]">Assign to Restaurants</h2>
                <p className="text-[12px] text-slate-500">Catalog items have no warehouse. Select third-party restaurants where this item should be available.</p>
              </div>
              <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <div><Label>Restaurant Price Override</Label><Input value={form.restaurantPrice} onChange={(e) => { const value = e.target.value; if (/^\d{0,8}(\.\d{0,2})?$/.test(value)) setField("restaurantPrice", value); }} placeholder="optional" inputMode="decimal" /></div>
                <div><Label>Preparation Time</Label><Input value={form.preparationTimeMins} onChange={(e) => { const value = e.target.value; if (/^\d{0,4}$/.test(value)) setField("preparationTimeMins", value); }} inputMode="numeric" /></div>
                <div><Label>Available</Label><Toggle value={form.isAvailable} onChange={(v) => setField("isAvailable", v)} /></div>
              </div>
              <RestaurantMultiSelect
                restaurants={restaurants}
                selectedIds={form.restaurantIds}
                search={form.restaurantSearch}
                onSearch={(value) => setField("restaurantSearch", value)}
                onToggle={toggleRestaurant}
                onClear={() => setField("restaurantIds", [])}
              />
            </section>

            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => navigate("/admin/food-dashboard/items")}>Cancel</Button>
              <Button onClick={submit} disabled={saving}>{saving ? "Saving..." : editMode ? "Update Food" : "Save Food"}</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
